import type { Collection, IndexProxy } from '@tanstack/db';
import { compact } from 'es-toolkit';
import type { RxCollection, RxJsonSchema } from 'rxdb';

import { deserializeActionData, type SerializedActionPayload, serializeActionData } from '@/lib/actions/serialization';
import { tryAcceptCollectionMutations } from '@/lib/cache/collection-utils';
import { registerLocalCacheCollection } from '@/lib/cache/databases/local-cache.db';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import type { ProfileResponse } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';

import {
  COLLECTION_ID,
  DEBUG_NAMESPACE,
  LEGACY_STORAGE_KEY,
  type UserPreviewCacheRecord,
  type UserPreviewCacheStorageRecord,
} from './user-preview-cache.types';

const userPreviewCacheRxSchema: RxJsonSchema<UserPreviewCacheStorageRecord> = {
  title: 'user-preview-cache',
  version: 0,
  primaryKey: {
    key: 'primaryCacheKey',
    fields: ['primaryCacheKey'],
    separator: '||',
  },
  type: 'object',
  properties: {
    primaryCacheKey: { type: 'string', maxLength: 200 },
    profileId: { type: ['string', 'null'] as const, maxLength: 64 },
    usernameKey: { type: ['string', 'null'] as const, maxLength: 64 },
    preview: { type: ['object', 'null'] as const, additionalProperties: true },
    profile: { type: ['object', 'null'] as const, additionalProperties: true },
    error: { type: 'boolean' },
    fetchedAt: { type: 'number' },
    detail: { type: 'string', enum: ['partial', 'full'] },
    aliases: {
      type: 'array',
      items: { type: 'string' },
      uniqueItems: true,
    },
  },
  required: ['primaryCacheKey', 'error', 'fetchedAt', 'detail', 'aliases'],
  additionalProperties: false,
};

export type UserPreviewCacheStorageContext = {
  rxCollection: RxCollection<UserPreviewCacheStorageRecord>;
  collection: Collection<UserPreviewCacheStorageRecord, string>;
  usernameIndex: IndexProxy<string>;
  acceptCollectionTransaction: (tx: unknown) => void;
  pruneExpiredRecords: () => Promise<void>;
};

export const serializeCacheRecord = (record: UserPreviewCacheRecord): UserPreviewCacheStorageRecord => {
  const serialized: UserPreviewCacheStorageRecord = {
    primaryCacheKey: record.primaryCacheKey,
    error: record.error,
    fetchedAt: record.fetchedAt,
    detail: record.detail,
    aliases: record.aliases,
  };

  if (record.profileId !== undefined) {
    serialized.profileId = record.profileId;
  }

  if (record.usernameKey !== undefined) {
    serialized.usernameKey = record.usernameKey;
  }

  if (record.preview) {
    serialized.preview = serializeActionData(record.preview);
  }

  if (record.profile) {
    serialized.profile = serializeActionData(record.profile);
  }

  return serialized;
};

const assignOptionalProperty = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined
): void => {
  if (value === undefined) {
    Reflect.deleteProperty(target, key);
    return;
  }
  target[key] = value;
};

const pruneExpiredRecordsFactory =
  (cacheTtlMs: number) =>
  async (collection: RxCollection<UserPreviewCacheStorageRecord>): Promise<void> => {
    const docs = await collection.find().exec();
    if (!Array.isArray(docs) || docs.length === 0) {
      return;
    }
    const cutoff = Date.now() - cacheTtlMs;
    const expired = docs.filter((document_) => {
      try {
        const data = document_.toJSON();
        return typeof data.fetchedAt === 'number' && data.fetchedAt < cutoff;
      } catch {
        return false;
      }
    });
    if (expired.length === 0) {
      return;
    }
    await Promise.all(
      expired.map(async (document_) => {
        try {
          await document_.remove();
        } catch {
          /** ignore removal failures */
        }
      })
    );
  };

export const clearLegacyLocalStorage = (): void => {
  if (globalThis.window === undefined || !globalThis.localStorage) {
    return;
  }
  try {
    globalThis.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (error) {
    registerDebugNamespace(DEBUG_NAMESPACE, {
      warn: (...arguments_: unknown[]) => {
        console.warn('[user-preview-cache]', ...arguments_);
      },
    });
    console.warn('[user-preview-cache] Failed to remove legacy localStorage cache', error);
  }
};

export async function createUserPreviewCacheStorage(cacheTtlMs: number): Promise<UserPreviewCacheStorageContext> {
  const pruneExpiredRecords = pruneExpiredRecordsFactory(cacheTtlMs);
  const { rxCollection, collection } = await registerLocalCacheCollection<UserPreviewCacheStorageRecord>(
    COLLECTION_ID,
    {
      schema: userPreviewCacheRxSchema,
      prune: async ({ rxCollection: targetCollection }) => {
        await pruneExpiredRecords(targetCollection);
      },
      onReady: () => {
        clearLegacyLocalStorage();
      },
    }
  );

  await pruneExpiredRecords(rxCollection);
  clearLegacyLocalStorage();

  const usernameIndex: IndexProxy<string> = collection.createIndex((row) => row.usernameKey, {
    name: 'username',
  });

  try {
    await usernameIndex.whenReady();
  } catch {
    // ignore index creation failures, we fall back to scans
  }

  const acceptCollectionTransaction = (tx: unknown) => {
    tryAcceptCollectionMutations(collection, tx, { suppressDuplicate: true });
  };

  return {
    rxCollection,
    collection,
    usernameIndex,
    acceptCollectionTransaction,
    pruneExpiredRecords: () => pruneExpiredRecords(rxCollection),
  };
}

export const inflateOptionalProperty = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined
): void => {
  if (value === undefined) {
    return;
  }
  target[key] = value;
};

export const assignStorageRecord = (
  draftRecord: UserPreviewCacheStorageRecord,
  serialized: UserPreviewCacheStorageRecord
): void => {
  draftRecord.primaryCacheKey = serialized.primaryCacheKey;
  draftRecord.error = serialized.error;
  draftRecord.detail = serialized.detail;
  draftRecord.fetchedAt = serialized.fetchedAt;
  draftRecord.aliases = compact(serialized.aliases);
  assignOptionalProperty(draftRecord, 'profileId', serialized.profileId);
  assignOptionalProperty(draftRecord, 'usernameKey', serialized.usernameKey);
  assignOptionalProperty(draftRecord, 'preview', serialized.preview);
  assignOptionalProperty(draftRecord, 'profile', serialized.profile);
};

export const deserializePayload = <T>(payload: SerializedActionPayload | undefined | null): T | undefined => {
  if (payload == null) {
    return;
  }
  return deserializeActionData<T>(payload);
};

export const deserializeCacheRecord = (
  record: UserPreviewCacheStorageRecord | null | undefined
): UserPreviewCacheRecord | undefined => {
  if (record == null) {
    return;
  }
  const preview = deserializePayload<UserPreviewVM>(record.preview);
  const profile = deserializePayload<ProfileResponse>(record.profile);

  const hydrated: UserPreviewCacheRecord = {
    primaryCacheKey: record.primaryCacheKey,
    error: record.error,
    fetchedAt: record.fetchedAt,
    detail: record.detail,
    aliases: record.aliases,
  };
  if (record.profileId !== undefined && record.profileId !== null) {
    hydrated.profileId = record.profileId;
  }
  if (record.usernameKey !== undefined && record.usernameKey !== null) {
    hydrated.usernameKey = record.usernameKey;
  }
  if (preview !== undefined) {
    hydrated.preview = preview;
  }
  if (profile !== undefined) {
    hydrated.profile = profile;
  }

  return hydrated;
};
